import { useState, useCallback, useEffect, useRef, forwardRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { X, AlertTriangle, RefreshCw } from 'lucide-react'
import { DOMAIN_ICONS } from './domainIcons'
import { DOMAIN_LABELS } from '@/types/contextEngine'
import type { DriftEvent } from '@/types/contextEngine'

const AUTO_DISMISS_MS = 8000

// ─── Single toast ────────────────────────────────────────────────────────────

interface DriftToastProps {
  event: DriftEvent
  onDismiss: (id: string) => void
}

const DriftToast = forwardRef<HTMLDivElement, DriftToastProps>(function DriftToast({ event, onDismiss }, ref) {
  const navigate = useNavigate()
  const timerRef = useRef<ReturnType<typeof setTimeout>>()
  const id = event.conflictId ?? event.chunkId ?? event.timestamp

  const DomainIcon = DOMAIN_ICONS[event.domain]
  const isConflict = event.eventType === 'CONFLICT_DETECTED'
  const EventIcon = isConflict ? AlertTriangle : RefreshCw

  useEffect(() => {
    timerRef.current = setTimeout(() => onDismiss(id), AUTO_DISMISS_MS)
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current)
    }
  }, [id, onDismiss])

  const handleClick = useCallback(() => {
    if (isConflict && event.conflictId) {
      navigate('/context/conflicts')
    } else {
      navigate(`/context/domains/${event.domain}`)
    }
    onDismiss(id)
  }, [navigate, event, isConflict, id, onDismiss])

  return (
    <motion.div
      ref={ref}
      layout
      initial={{ opacity: 0, x: 80, scale: 0.95 }}
      animate={{ opacity: 1, x: 0, scale: 1 }}
      exit={{ opacity: 0, x: 80, scale: 0.95 }}
      transition={{ duration: 0.35, ease: [0, 0, 0.2, 1] }}
      className="pointer-events-auto flex w-80 items-start gap-3 rounded-lg border border-border bg-bg-elevated p-4 shadow-lg"
      role="alert"
      aria-live="polite"
    >
      {/* Domain icon with amber tint */}
      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-accent-amber/10">
        <DomainIcon size={16} className="text-accent-amber" />
      </div>

      {/* Content */}
      <button
        onClick={handleClick}
        className="flex-1 text-left"
      >
        <div className="mb-0.5 flex items-center gap-1.5 text-xs font-medium text-accent-amber">
          <EventIcon size={12} />
          {DOMAIN_LABELS[event.domain]}
        </div>
        <p className="text-sm leading-snug text-text-primary line-clamp-2">
          {event.description}
        </p>
        <span className="mt-1 text-xs text-text-tertiary">Click to review</span>
      </button>

      {/* Dismiss */}
      <button
        onClick={(e) => {
          e.stopPropagation()
          onDismiss(id)
        }}
        className="shrink-0 rounded p-1 text-text-tertiary transition-colors hover:bg-bg-surface hover:text-text-secondary"
        aria-label="Dismiss notification"
      >
        <X size={14} />
      </button>
    </motion.div>
  )
})

// ─── Toast container ─────────────────────────────────────────────────────────

export function useDriftToasts() {
  const [toasts, setToasts] = useState<DriftEvent[]>([])

  const addToast = useCallback((event: DriftEvent) => {
    setToasts((prev) => {
      // Deduplicate by conflictId or chunkId
      const key = event.conflictId ?? event.chunkId ?? event.timestamp
      if (prev.some((t) => (t.conflictId ?? t.chunkId ?? t.timestamp) === key)) return prev
      // Max 3 visible toasts
      return [...prev.slice(-2), event]
    })
  }, [])

  const dismissToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => (t.conflictId ?? t.chunkId ?? t.timestamp) !== id))
  }, [])

  return { toasts, addToast, dismissToast }
}

// ─── Toast list renderer ─────────────────────────────────────────────────────

interface DriftToastListProps {
  toasts: DriftEvent[]
  onDismiss: (id: string) => void
}

export function DriftToastList({ toasts, onDismiss }: DriftToastListProps) {
  return (
    <div className="pointer-events-none fixed right-4 top-4 z-50 flex flex-col gap-3">
      <AnimatePresence mode="popLayout">
        {toasts.map((event) => (
          <DriftToast
            key={event.conflictId ?? event.chunkId ?? event.timestamp}
            event={event}
            onDismiss={onDismiss}
          />
        ))}
      </AnimatePresence>
    </div>
  )
}
